import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import {TableIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useMemo} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {formatIssueReference} from '../utils/format-issue-reference'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {ClosedEvent$key} from './__generated__/ClosedEvent.graphql'
import styles from './ClosedEvent.module.css'
import {IssueEventRationale} from './IssueEventRationale'
import {IssueLink} from './IssueLink'
import {TimelineRow} from './row/TimelineRow'

export type ClosedEventProps = {
  queryRef: ClosedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  showStateReason?: boolean
  repositoryId: string
  ownerLogin: string
}

export function ClosedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  timelineEventBaseUrl,
  highlightedEventId,
  refAttribute,
  showStateReason = true,
  repositoryId,
  ownerLogin,
}: ClosedEventProps): React.ReactElement {
  const {actor, createdAt, stateReason, databaseId, closer, closingProjectItemStatus, duplicateOf, intent} =
    useFragment(
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      graphql`
        fragment ClosedEvent on ClosedEvent {
          databaseId
          createdAt
          stateReason
          duplicateOf {
            ...IssueLink @dangerously_unaliased_fixme
          }
          closingProjectItemStatus
          closer {
            __typename
            ... on ProjectV2 {
              url
              title
            }
            ... on PullRequest {
              url
              number
              repository {
                id
                name
                owner {
                  login
                }
              }
            }
            ... on Commit {
              url
              abbreviatedOid
              repository {
                name
                owner {
                  login
                }
              }
            }
          }
          actor {
            ...TimelineRowEventActor
          }
          intent {
            rationale
          }
        }
      `,
      queryRef,
    )

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const highlighted = String(databaseId) === highlightedEventId
  const {getStateQuery, issueStateTimelineIcon: icon, issueStateColor} = useIssueState({state: 'CLOSED', stateReason})
  const {stateChangeQuery, stateReasonString} = getStateQuery()

  const queryUrl = useMemo(() => {
    return `${timelineEventBaseUrl}?q=${stateReason && encodeURIComponent(stateChangeQuery)}`
  }, [stateChangeQuery, stateReason, timelineEventBaseUrl])

  const closedByCommit = closer?.__typename === 'Commit'
  const closedByMemexProject = closer?.__typename === 'ProjectV2'
  const closerPullRequestUrl = closer && closer.__typename === 'PullRequest' ? closer.url : undefined
  const closerCommitUrl = closedByCommit ? closer.url : undefined
  const closerMemexProjectUrl = closedByMemexProject ? closer.url : undefined

  const closerPullRequestReference =
    closer && closer.__typename === 'PullRequest'
      ? formatIssueReference(
          {
            number: closer.number,
            ownerLogin: closer.repository.owner.login,
            repoId: closer.repository.id,
            repoName: closer.repository.name,
          },
          repositoryId,
          ownerLogin,
        )
      : undefined

  const hovercardAttributes = closer
    ? closer.__typename === 'Commit'
      ? getHovercardAttributesForType('commit', {
          owner: closer.repository.owner.login,
          repo: closer.repository.name,
          commitish: closer.abbreviatedOid,
        })
      : closer.__typename === 'PullRequest'
        ? getHovercardAttributesForType('pull_request', {
            owner: closer.repository.owner.login,
            repo: closer.repository.name,
            pullRequestNumber: closer.number,
          })
        : {}
    : {}

  const closerLink = closedByMemexProject ? (
    <>
      <Octicon icon={TableIcon} />{' '}
      <Link href={closerMemexProjectUrl} data-testid="closer-link" inline className={styles.stateReasonLink}>
        {closer.title}
      </Link>
    </>
  ) : (
    <Link
      href={closedByCommit ? closerCommitUrl : closerPullRequestUrl}
      {...hovercardAttributes}
      data-testid="closer-link"
      className={styles.closerLink}
    >
      {closedByCommit ? `${closer.abbreviatedOid}` : closerPullRequestReference}
    </Link>
  )

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={icon}
      iconColoring={{backgroundColor: issueStateColor, color: 'var(--fgColor-onEmphasis)'}}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.closedThis} `}
        {stateReason && showStateReason && (
          <>
            {`${LABELS.timeline.as} ${duplicateOf ? ' a ' : ''}`}
            <Link data-testid="state-reason-link" href={queryUrl} inline className={styles.stateReasonLink}>
              {stateReasonString}
            </Link>
          </>
        )}
        {closer && (
          <>
            {closedByMemexProject ? `by moving to ${closingProjectItemStatus} ` : ''}
            {`${LABELS.timeline.in} `}
            {closerLink}
          </>
        )}
        {duplicateOf && (
          <>
            {`${LABELS.timeline.of} `}
            <IssueLink inline data={duplicateOf} targetRepositoryId={repositoryId} targetOwnerLogin={ownerLogin} />{' '}
          </>
        )}
        {intent?.rationale && (
          <IssueEventRationale
            rationale={intent.rationale}
            issueEventDatabaseId={String(databaseId)}
            analyticsAction="ISSUE_CLOSED"
            target={stateReason}
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
